/**
 * Pickleball Detection Interpolation Utilities
 *
 * Shared functions for predicting missing ball/paddle detections.
 * Used by both feature extraction (xgboostPredictor) and visualization (PickleballVisualizerStrategy).
 *
 * Prediction strategy (real-time compatible):
 * - If both frame-2 and frame-1 exist: use velocity extrapolation
 *   Predicted[frame] = Position[frame-1] + (Position[frame-1] - Position[frame-2])
 * - If only frame-1 exists: use frame-1's position (assume stationary)
 * - Returns -1 if frame-1 doesn't exist
 */

// Minimum confidence threshold for valid detection
export const CONFIDENCE_THRESHOLD = 0.3;

/**
 * Check if a detection has sufficient confidence
 * @param {Object|null} det - Detection object (ball or paddle)
 * @returns {boolean}
 */
export function isValidDetection(det) {
  if (!det) return false;
  return (det.confidence || 0) > CONFIDENCE_THRESHOLD;
}

/**
 * Get primary (highest confidence) ball and paddle from frame
 *
 * @param {Object} frame - Frame object
 * @returns {Object} { ball, paddle } or nulls if missing
 */
export function getPrimaryDetections(frame) {
  if (!frame) return { ball: null, paddle: null };

  // Get balls - support both legacy (ball) and new (balls) format
  let balls = frame.balls || [];
  if (balls.length === 0 && frame.ball) {
    balls = [frame.ball];
  }

  // Get paddles - support both legacy (paddle) and new (paddles) format
  let paddles = frame.paddles || [];
  if (paddles.length === 0 && frame.paddle) {
    paddles = [frame.paddle];
  }

  // Sort by confidence and get highest
  if (balls.length > 0) {
    balls = [...balls].sort((a, b) => (b.confidence || 0) - (a.confidence || 0));
  }
  if (paddles.length > 0) {
    paddles = [...paddles].sort((a, b) => (b.confidence || 0) - (a.confidence || 0));
  }

  return {
    ball: balls[0] || null,
    paddle: paddles[0] || null
  };
}

/**
 * Predict detection position using previous frames
 * - If both frame-2 and frame-1 exist: use velocity extrapolation
 *   Predicted[frame] = 2 * Position[frame-1] - Position[frame-2]
 * - If only frame-1 exists: use frame-1's position (assume stationary)
 * - If neither exists: return -1
 *
 * @param {Object|null} detMinus2 - Detection at frame-2
 * @param {Object|null} detMinus1 - Detection at frame-1
 * @returns {Object|number} Predicted detection or -1 if not enough data
 */
export function predictDetection(detMinus2, detMinus1) {
  const validMinus2 = isValidDetection(detMinus2);
  const validMinus1 = isValidDetection(detMinus1);

  // If frame-1 is not valid, we can't predict
  if (!validMinus1) {
    return -1;
  }

  // If only frame-1 is valid, use its position (assume stationary)
  if (!validMinus2) {
    return {
      x: detMinus1.x,
      y: detMinus1.y,
      width: detMinus1.width,
      height: detMinus1.height,
      confidence: detMinus1.confidence || 0,
      predicted: true
    };
  }

  // Both frames valid: use velocity extrapolation
  // Predicted position = frame-1 + velocity = 2*frame-1 - frame-2
  return {
    x: 2 * detMinus1.x - detMinus2.x,
    y: 2 * detMinus1.y - detMinus2.y,
    width: detMinus1.width || detMinus2.width,
    height: detMinus1.height || detMinus2.height,
    confidence: Math.min(detMinus1.confidence || 0, detMinus2.confidence || 0),
    predicted: true
  };
}

/**
 * Legacy function name for backwards compatibility
 * @deprecated Use predictDetection instead
 */
export function interpolateDetection(detMinus2, detMinus1, _detPlus1, _detPlus2) {
  const result = predictDetection(detMinus2, detMinus1);
  // Convert -1 to null for backwards compatibility
  return result === -1 ? null : result;
}

// Maximum chain depth for predictions (how many frames back we try to find valid detections)
// This affects both recursion depth and history buffer size in streaming mode
export const MAX_CHAIN_DEPTH = 10;

/**
 * Get predicted detection from frame array with chaining support
 * Predictions can chain: if frame-1 was predicted, use that prediction to predict current frame
 *
 * @param {Array} frames - Array of frame objects
 * @param {number} centerIdx - Index of the current frame in the frames array
 * @param {string} type - 'ball' or 'paddle'
 * @param {Object} options - Optional parameters
 * @param {number} options.maxChainDepth - Maximum chain depth (default: MAX_CHAIN_DEPTH)
 * @param {Object} options.historyBuffer - DetectionRingBuffer for frames before buffer[0]
 * @returns {Object} { detection, wasInterpolated } - detection is -1 if not enough data to predict
 */
export function getInterpolatedDetection(frames, centerIdx, type, options = {}) {
  // Handle legacy API: if options is a number, treat it as maxChainDepth
  const maxChainDepth = typeof options === 'number' ? options : (options.maxChainDepth || MAX_CHAIN_DEPTH);
  const historyBuffer = typeof options === 'object' ? (options.historyBuffer || null) : null;

  // Get raw detection at an index (no prediction)
  const getRawDetAt = (idx) => {
    // Check history buffer for negative indices
    if (idx < 0 && historyBuffer) {
      const cached = historyBuffer.get(idx);
      if (cached) {
        return type === 'ball' ? cached.ball : cached.paddle;
      }
      return null;
    }
    if (idx >= 0 && idx < frames.length) {
      const { ball, paddle } = getPrimaryDetections(frames[idx]);
      return type === 'ball' ? ball : paddle;
    }
    return null;
  };

  // Get detection at an index, with prediction if needed (recursive with depth limit)
  const getDetAt = (idx, depth) => {
    // Allow negative indices up to -maxChainDepth if we have history buffer
    const minIdx = historyBuffer ? -historyBuffer.getCount() : 0;
    if (idx < minIdx || idx >= frames.length) return null;

    // Check for valid raw detection FIRST - return even at depth=0
    const raw = getRawDetAt(idx);
    if (isValidDetection(raw)) {
      return raw;
    }

    // Only check depth limit for prediction (not for raw valid detections)
    if (depth <= 0) return null; // Prevent infinite recursion

    // Try to predict this position using idx-2 and idx-1
    const detMinus2 = getDetAt(idx - 2, depth - 1);
    const detMinus1 = getDetAt(idx - 1, depth - 1);
    const predicted = predictDetection(detMinus2, detMinus1);

    return predicted === -1 ? null : predicted;
  };

  // Check current frame first
  const current = getRawDetAt(centerIdx);
  if (isValidDetection(current)) {
    return { detection: current, wasInterpolated: false };
  }

  // Predict using frame-2 and frame-1 (with chaining support)
  const detMinus2 = getDetAt(centerIdx - 2, maxChainDepth);
  const detMinus1 = getDetAt(centerIdx - 1, maxChainDepth);
  const predicted = predictDetection(detMinus2, detMinus1);

  // Return -1 if prediction failed, otherwise return the prediction
  if (predicted === -1) {
    return { detection: -1, wasInterpolated: false };
  }

  return { detection: predicted, wasInterpolated: true };
}

/**
 * Get both ball and paddle with prediction for a given frame
 *
 * @param {Array} frames - Array of frame objects
 * @param {number} frameIdx - Index of the frame
 * @returns {Object} { ball, paddle, ballInterpolated, paddleInterpolated }
 *                   ball/paddle can be -1 if not enough data to predict
 */
export function getInterpolatedDetections(frames, frameIdx) {
  const ballResult = getInterpolatedDetection(frames, frameIdx, 'ball');
  const paddleResult = getInterpolatedDetection(frames, frameIdx, 'paddle');

  return {
    ball: ballResult.detection,
    paddle: paddleResult.detection,
    ballInterpolated: ballResult.wasInterpolated,
    paddleInterpolated: paddleResult.wasInterpolated
  };
}
